import { changeLang } from '@sdk/lang/gettext';
import { LANGUAGE_DATA_KEY } from '@sdk/common/constants';
import removeAuthCookie from '@sdk/api/removeAuthCookie';
import removeOrgIdCookie from '@sdk/api/removeOrgIdCookie';
import getAuthConfig from '@sdk/api/utils/getAuthConfig';
import getOrgIdConfig from '@sdk/api/utils/getOrgIdConfig';
import { shouldImpersonate, removeImpersonation } from '@sdk/authentication/impersonate';

import config from '../../config';

import { setUserDefaults } from './userDefaults';

const { ORG_ID_COOKIE_NAME, USE_ORG_ID_IN_API_ROUTES } = config;

export default function Authentication(
  Restangular,
  RestangularStatus,
  Utils,
  CurrencyService,
  AlertService,
  sdk,
  authService,
  uibDatepickerConfig,
  gettextCatalog,
  $injector,
  $rootScope,
  $cookies,
  $urlRouter,
  $state,
  $localStorage,
  $uibModal,
  $q,
  WindowLocation,
  $location,
  $http
) {
  'ngInject';

  let _currentUser = null;
  let _token = null;
  let _ip = null;
  let _orgId = null;
  let _isWhitelisted = false;
  let _intercomUserHash = null;
  let _calendarSocketRoom = null;
  let _baseUrl = config.API_END_POINT;
  let _userLoadedPromise = null;
  let _resolveUserLoadedPromise = null;
  let _isPrefab = null;
  let _googleCalendarExpired = null;
  let _firstRun = false;
  let _userCountLimits = null;
  const _onOrgSettingsChangedHandlers = [];
  const _onOrgUpdateHandlers = [];
  let StatusService;

  let _Socket = null;
  let _UserUtils = null;

  function _getSocket() {
    if (!_Socket) {
      _Socket = $injector.get('Socket');
    }

    return _Socket;
  }

  // This listener will update both $localStorage and current user settings
  // whenever browser's localStorage client settings item is changed outside angularjs
  window.addEventListener('storage', (e) => {
    if (e.key === 'ngStorage-clientsettings') {
      const newValue = JSON.parse(e.newValue);
      $localStorage.clientsettings = newValue;
      _currentUser.clientsettings = newValue;
    }
  });

  function _log_out_user() {
    $rootScope.$broadcast('event:auth-logout');
    removeAuthCookie();
    removeOrgIdCookie();
    delete $localStorage.clientcache;
    _token = null;
    _orgId = null;
  }

  function _changeUser(user) {
    const _oldUser = _currentUser;
    let isNewUser = false;

    if (user) {
      user.isAuthenticationDotUser = true;
      Restangular.restangularizeElement(null, user, 'users');
      user.isMe = true;
      // Needed to avoid errors is react model
      if (user?.groups) {
        user.locations = user.groups.filter((g) => g.type === 'location');
        user.locationIds = user.locations.map((g) => g.id);
        user.positions = user.groups.filter((g) => g.type === 'position');
        user.allTypeGroupIds = user.groups.map((g) => g.id);
      }

      const isExistingUser = setUserDefaults(user, $localStorage);
      if (!isExistingUser) {
        $injector.get('AnnouncementService').skipForNewUser(user);
        isNewUser = true;

        user.settings.clientFlags.autoAssigningAnnouncementModalIrrelevant = true;
      }

      uibDatepickerConfig.startingDay = user.orgSettings.firstDayOfWeek;
    }

    _currentUser = user;

    if (!__TEST__ && isNewUser) {
      syncSettings();
    }

    _updateCalendarSocketRoom(_oldUser);

    if (user) {
      CurrencyService.setOrgCurrency(user.orgSettings.currency);
    } else {
      _log_out_user();
    }
  }

  function _updateLanguage(langaugeChanged, language) {
    if (!langaugeChanged) {
      return;
    }

    $localStorage[LANGUAGE_DATA_KEY] = language;
    gettextCatalog.currentLanguage = language;
    changeLang(language);
  }

  function _updateCalendarSocketRoom(oldUser) {
    if (!_currentUser) {
      if (_calendarSocketRoom) {
        _sendCalendarSocketCommand(_calendarSocketRoom, 'leave', oldUser);
      }

      return;
    }

    _calendarSocketRoom = 'scratchpad';
    _sendCalendarSocketCommand(_calendarSocketRoom, 'join', _currentUser);
  }

  function _sendCalendarSocketCommand(roomType, command, user) {
    const payload = {
      command,
      room: `${roomType}:${user.org.id}`,
    };

    if (command === 'join') {
      payload.ignore = _token;
    }

    _getSocket().send(payload);
  }

  function _resolveUserLoaded() {
    if (_resolveUserLoadedPromise) {
      _resolveUserLoadedPromise(_currentUser);
    }
  }

  function _resetUserLoadedPromise() {
    _userLoadedPromise = $q((resolve) => {
      _resolveUserLoadedPromise = resolve;
    });
  }

  async function _switchOrg(org) {
    if (!StatusService) {
      StatusService = $injector.get('StatusService');
    }

    const data = await Restangular.all('account').customPUT({ orgId: org.id }, 'session');
    delete $localStorage.clientcache;
    $cookies.put(ORG_ID_COOKIE_NAME, data.org.id);

    // Just go to dashboard, reloads all states and collections
    WindowLocation.href = '/';
  }

  function _parseOrgDates(org) {
    [
      'premiumTrialStartDate',
      'subscriptionStartDate',
      'subscriptionCancelDate',
      'subscriptionExpiredDate',
      'nextChargeDate',
      'subscriptionCreditCardExpiryDate',
      'subscriptionPaymentFailedDate',
    ].forEach((dateProp) => {
      if (org[dateProp]) {
        org[dateProp] = new Date(org[dateProp]);
      }
    });
  }

  $rootScope.$on('socket:org:edit', (event, data) => {
    if (!__TEST__) {
      console.log('org:edit');
      console.log(data);
    }

    if (_currentUser.org && data.id === _currentUser.org.id) {
      AuthenticationFactory.updateCurrentOrg(data);
      _changeUser(_currentUser);
    }
  });

  async function syncSettings(newSettings, { didWorldClockChange = false } = {}) {
    const worldClockChanged =
      didWorldClockChange ||
      (newSettings && newSettings.worldClockEnabled !== _currentUser.settings.worldClockEnabled);
    const languageChanged = newSettings && newSettings.language !== _currentUser?.settings.language;
    _currentUser.settings = await Restangular.all('account').customPUT(
      newSettings || _currentUser.settings,
      'settings'
    );
    _changeUser(_currentUser);
    _updateLanguage(languageChanged, newSettings?.language);
    $rootScope.$broadcast('event:user-settings-changed', worldClockChanged);
    return true;
  }

  const AuthenticationFactory = {
    get token() {
      return _token;
    },

    set token(val) {
      if (__TEST__) {
        _token = val;
      }
    },

    get ip() {
      return _ip;
    },

    get baseUrl() {
      return _baseUrl;
    },

    get user() {
      return _currentUser;
    },

    get userLoaded() {
      return _userLoadedPromise;
    },

    set userLoaded(value) {
      if (__TEST__) {
        _userLoadedPromise = value;
      }
    },

    set user(value) {
      if (__TEST__) {
        _currentUser = value;
      }
    },

    get authorizationHeaders() {
      return {
        headers: {
          Authorization: _token,
        },
      };
    },

    get isPrefab() {
      return _isPrefab;
    },

    set isPrefab(val) {
      if (__TEST__) {
        _isPrefab = val;
      }
    },

    get isOrgIdEven() {
      // for A/B tests
      return _currentUser?.org.id % 2 === 0;
    },

    get isWhitelisted() {
      return _isWhitelisted;
    },

    get intercomUserHash() {
      return _intercomUserHash;
    },

    get isOldPremium() {
      return _currentUser && _currentUser.orgSettings.isOldPremium;
    },

    get googleCalendarExpired() {
      return _googleCalendarExpired;
    },

    set googleCalendarExpired(value) {
      _googleCalendarExpired = value;
    },

    get firstRun() {
      return _firstRun;
    },

    set firstRun(value) {
      _firstRun = value;
    },

    get userCountLimits() {
      return _userCountLimits;
    }
  };

  $rootScope.$on('sdk:client-flags:save', (e, data) => {
    _currentUser.settings.clientFlags = {
      ..._currentUser.settings.clientFlags,
      ...data,
    };

    _changeUser(_currentUser);
    console.info('user has changed', _currentUser.settings.clientFlags);
    $rootScope.$broadcast('event:user-settings-changed');
  });

  AuthenticationFactory.saveClientFlags = async function saveClientFlags(flags) {
    const oldFlags = JSON.parse(JSON.stringify(_currentUser.settings.clientFlags));
    _currentUser.settings.clientFlags = {
      ..._currentUser.settings.clientFlags,
      ...flags,
    };
    _changeUser(_currentUser);
    $rootScope.$broadcast('event:user-settings-changed');

    try {
      await AuthenticationFactory.sendRequest({
        path: 'account/settings/client_flags',
        method: 'put',
        payload: flags,
      });
    } catch {
      _currentUser.settings.clientFlags = oldFlags;
      _changeUser(_currentUser);
      $rootScope.$broadcast('event:user-settings-changed');
    }
  };

  AuthenticationFactory.authorize = function authorize(accessLevel) {
    if ('isManager' in accessLevel) {
      if (accessLevel.isManager !== AuthenticationFactory.user.isManager) {
        return false;
      }
    }

    if ('isAdmin' in accessLevel) {
      if (accessLevel.isAdmin !== AuthenticationFactory.user.isAdmin) {
        return false;
      }
    }

    return true;
  };

  AuthenticationFactory.init = async function init(cb) {
    // Get the token from the session
    const token = getAuthConfig();
    const orgId = getOrgIdConfig();

    if (!orgId && USE_ORG_ID_IN_API_ROUTES) {
      return AuthenticationFactory.logout(true, true);
    }

    _resetUserLoadedPromise();

    // Save the token
    _token = token;

    // Save orgId
    if (USE_ORG_ID_IN_API_ROUTES) {
      _orgId = orgId;
      _baseUrl = `${Restangular.configuration.baseUrl}/${orgId}/`;
      Restangular.setBaseUrl(_baseUrl);
    }

    Restangular.setDefaultHeaders({ Authorization: token });

    await AuthenticationFactory.loadSessionFromServer();

    if (cb) {
      cb();
    }
  };

  async function _getSession() {
    const data = await Restangular.all('account').one('session').get();
    _parseOrgDates(data.org);
    return data;
  }

  AuthenticationFactory.loadSessionFromServer = async function loadSessionFromServer() {
    const search = $location.search();
    if (search && search.org) {
      _switchOrg({ id: search.org });
    } else {
      const data = await _getSession();
      _googleCalendarExpired = data.googleCalendarExpired;
      _isWhitelisted = data.isWhitelisted;
      _intercomUserHash = data.intercomUserHash;
      _userCountLimits = data.userCountLimits;

      if (USE_ORG_ID_IN_API_ROUTES && String(data.org.id) !== String(_orgId)) {
        AuthenticationFactory.logout(true, true);
      }

      _isPrefab = data.isPrefab;
      _ip = data.ip;
      data.user.org = data.org;
      data.user.org.isSlingOrg = this.isSlingOrg(data.org);

      // The following line is handy for testing
      if (config.FORCE_NON_PREMIUM) {
        data.org.isPremium = false;
        data.org.subscriptionLevel = 0;
        data.org.isBusinessSubscription = false;
        data.user.hasAccessToLaborCost = false;
        console.warn(
          '%c💣 BE WARNED ️️️️💣\nFORCED NON PREMIUM ACCOUNT VIA\nconfig.FORCE_NON_PREMIUM',
          'font-size: 30px;'
        );
      }

      _changeUser(data.user);
      if (data.user.orgSettings) {
        Restangular.restangularizeElement(null, data.user.orgSettings, 'account/org/settings');
      }

      _resolveUserLoaded();
    }
  };

  AuthenticationFactory.changeUser = _changeUser;
  AuthenticationFactory.switchOrg = _switchOrg;

  AuthenticationFactory.loginCancelled = function loginCancelled() {
    authService.loginCancelled();
  };

  AuthenticationFactory.logout = async function logout(
    ignoreServer,
    ignoreRedirect,
    preserveWindowLocation
  ) {
    if (!ignoreRedirect && !preserveWindowLocation) {
      $rootScope.loggedOut = true;
    }

    if (!ignoreServer) {
      // in case there's impersonation taking place,
      // this will destroy impersonated user session
      await Restangular.all('account').customDELETE('session');
    }

    if (shouldImpersonate()) {
      removeImpersonation();
      window.close();
      return;
    }

    _changeUser(null);
    _resetUserLoadedPromise();

    if (!preserveWindowLocation) {
      const searchParams = new URLSearchParams();
      const url = [];

      url.push(`${config.SLING_LOGIN_URL}${ignoreRedirect ? '' : '/bye'}`);
      if (WindowLocation.pathname?.length > 1) {
        searchParams.set('redirect', WindowLocation.href);
        searchParams.set('language', $localStorage[LANGUAGE_DATA_KEY]);
        url.push(searchParams.toString());
      }
      WindowLocation.href = url.join('?');
    }
  };

  AuthenticationFactory.syncSettings = syncSettings;

  AuthenticationFactory.syncClientSettings = function syncClientSettings() {
    $localStorage.clientsettings = _currentUser.clientsettings;
  };

  AuthenticationFactory.syncClientCache = function syncClientCache() {
    $localStorage.clientcache = _currentUser.clientcache;
  };

  AuthenticationFactory.updateOrg = function updateOrg(payload) {
    return Restangular.all(`/organisations/${_currentUser.org.id}`)
      .customPUT(payload)
      .then(() => {
        AuthenticationFactory.updateCurrentOrg(payload);
      });
  };

  AuthenticationFactory.setupOrg = async function setupOrg(payload) {
    const data = await Restangular.all(`/organisations/${_currentUser.org.id}/setup`).customPOST(
      payload
    );
    AuthenticationFactory.updateCurrentOrg(data);
  };

  AuthenticationFactory.updateCurrentOrg = function updateCurrentOrg(org) {
    _parseOrgDates(org);

    const orgInList = _currentUser.orgs.find((o) => o.id === _currentUser.org.id);
    const oldOrg = { ..._currentUser.org };
    Object.assign(_currentUser.org, org);
    Object.assign(orgInList, org);

    _onOrgUpdateHandlers.forEach((handler) => handler(org, oldOrg));

    if (org.settings) {
      Restangular.restangularizeElement(null, org.settings, 'account/org/settings');
      const oldSettings = _currentUser.orgSettings;
      _currentUser.orgSettings = org.settings;
      _onOrgSettingsChangedHandlers.forEach((handler) =>
        handler(_currentUser.orgSettings, oldSettings)
      );
    }
  };

  AuthenticationFactory.onOrgSettingsChanged = function onOrgSettingsChanged(handler) {
    _onOrgSettingsChangedHandlers.push(handler);

    return () => {
      const index = _onOrgSettingsChangedHandlers.indexOf(handler);
      if (index >= 0) {
        _onOrgSettingsChangedHandlers.splice(index, 1);
      }
    };
  };

  AuthenticationFactory.onOrgUpdate = function onOrgUpdate(handler) {
    _onOrgUpdateHandlers.push(handler);

    return () => {
      const index = _onOrgUpdateHandlers.indexOf(handler);
      if (index >= 0) {
        _onOrgUpdateHandlers.splice(index, 1);
      }
    };
  };

  AuthenticationFactory.isOrgFeatureEnabled = function isOrgFeatureEnabled(feature) {
    if (!_currentUser || config.FORCE_NON_PREMIUM) {
      return;
    }

    if (feature === 'autoAssigning' && _currentUser.orgSettings.isAutoAssigningTrialActive) {
      return true;
    }

    if ('orgFeatures' in _currentUser.orgSettings) {
      if (feature in _currentUser.orgSettings.orgFeatures) {
        return _currentUser.orgSettings.orgFeatures[feature];
      }

      console.warn(`UNEXPECTED FEATURES PASSED TO isOrgFeatureEnabled: "${feature}"`);
    }

    // fallback to "premiumFeatures"
    return (
      _currentUser.org.subscriptionLevel >= (_currentUser.orgSettings.premiumFeatures[feature] || 0)
    );
  };

  AuthenticationFactory.premiumExpired = async function premiumExpired() {
    AuthenticationFactory.user.org = (await _getSession()).org;
    $injector.get('EventCollection')._notifyOnChange();
  };

  AuthenticationFactory.resendVerify = async function resendVerify() {
    await Restangular.all('users/invite').post([{ id: _currentUser.id }]);
    AlertService.add(
      `
      <strong>Verification email sent!</strong><br />
      please check your email to verify
    `.trim(),
      null,
      'info'
    );
  };

  AuthenticationFactory.leaveSocketRooms = function leaveSocketRooms() {
    if (_token) {
      console.info('Logout from Cloak');
      _getSocket().send({
        command: 'leave',
        room: _token,
      });
    } else {
      console.info('Failed to logout from Cloak, no token!');
    }

    if (_calendarSocketRoom && (_orgId || !USE_ORG_ID_IN_API_ROUTES)) {
      _sendCalendarSocketCommand(_calendarSocketRoom, 'leave');
    }
  };

  AuthenticationFactory.consent = async function consent() {
    await $http.post(`${_baseUrl}account/consent`, {}, AuthenticationFactory.authorizationHeaders);
    _currentUser.consented = true;
  };

  AuthenticationFactory.updateCurrentUser = async function updateCurrentUser(user) {
    if (_currentUser && user) {
      _currentUser.consented = { ..._currentUser, ...user };
    }
  };

  AuthenticationFactory.sendRequest = function sendRequest({
    path,
    method = 'get',
    payload = {},
    params = {},
    httpConfig = {},
  }) {
    const url = `${_baseUrl}${path}${Utils.resolveQueryParams(params)}`;

    if (method === 'get' || method === 'delete') {
      return $http[method](url, { ...AuthenticationFactory.authorizationHeaders, ...httpConfig });
    }

    return $http[method](url, payload, {
      ...AuthenticationFactory.authorizationHeaders,
      ...httpConfig,
    });
  };

  AuthenticationFactory.isSlingOrg = function isSlingOrg(org) {
    if (__STAGING__ || __DEV__) {
      return true;
    }

    return (org || _currentUser?.org)?.id === 98931;
  };

  AuthenticationFactory.resendVerification = function resendVerification() {
    if (!_UserUtils) {
      _UserUtils = $injector.get('UserUtils');
    }

    return _UserUtils.resendVerification();
  };

  return AuthenticationFactory;
}
